import { z } from 'zod/v4';
import { format } from 'date-fns';
import type { Option } from '@org/ui';
import { WEEKDAY_OPTIONS, translateWeekdaysFromUTC, type WeekdayCode } from '@org/utils-client';

export type EventParticipant = {
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

export type EventGroupSummary = {
  groupId: string;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

export type EventClassSummary = {
  classId: string;
  name?: string | null;
  description?: string | null;
};

export type EventSummary = {
  eventId: string;
  title: string;
  description?: string | null;
  eventKind: 'group-class' | 'private-class' | 'closed-event' | 'open-event' | 'tutoring';
  eventCategoryId: string;
  hostUserId: string;
  location?: string | null;
  startTime: string | Date;
  endTime: string | Date;
  status?: 'scheduled' | 'cancelled' | 'completed' | 'draft';
  isTemplate?: boolean;
  seriesTemplateId?: string | null;
  recurrenceRule?: string | null;
  recurrenceEndsAt?: string | Date | null;
  groups?: EventGroupSummary[];
  classes?: EventClassSummary[];
  attendees?: EventParticipant[];
};

export const weekdayEnum = z.enum(WEEKDAY_OPTIONS.map((d) => d.code) as [string, ...string[]]);

const optionSchema = z.object({
  value: z.string().uuid(),
  label: z.string().min(1),
});

export const eventFormSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  eventKind: z.enum(['group-class', 'private-class', 'closed-event', 'open-event', 'tutoring']),
  eventCategoryId: z.string().uuid('Select a category'),
  hostUserId: z.string().uuid('Select a host'),
  location: z.string().optional(),
  startDateTime: z.string().min(1, 'Start time is required'),
  endDateTime: z.string().min(1, 'End time is required'),
  groupIds: z.array(optionSchema).default([]),
  classIds: z.array(optionSchema).default([]),
  attendeeIds: z.array(optionSchema).default([]),
  isRecurring: z.boolean().default(false),
  recurrenceFrequency: z.enum(['daily', 'weekly', 'monthly']).default('weekly'),
  recurrenceWeekdays: z.array(weekdayEnum).default([]),
  recurrenceEndDate: z.string().nullable().optional(),
  applyScope: z.enum(['occurrence', 'series']).optional(),
  forceResetAllAssociations: z.boolean().default(false),
  // Optional journey spread — only honored on recurring create (locked D-3).
  journeyId: z.string().optional().default(''),
});

export type EventFormValues = z.infer<typeof eventFormSchema>;

export const formatParticipantName = (participant: EventParticipant) => {
  const name = `${participant.firstName ?? ''} ${participant.lastName ?? ''}`.trim();
  return name || participant.email || 'Guest';
};

type OptionExtras = Record<string, string | boolean | undefined>;

export const toOption = <Extra extends OptionExtras = Record<string, never>>(
  value: string,
  label: string,
  extra?: Extra,
): Option & Extra => ({ value, label, ...(extra ?? ({} as Extra)) });

type OptionConfig<T, Extra extends OptionExtras> = {
  filter?: (item: T) => boolean;
  extend?: (item: T) => Extra;
};

export const buildOptions = <T, Extra extends OptionExtras = Record<string, never>>(
  items: readonly T[] | null | undefined,
  getValue: (item: T) => string,
  getLabel: (item: T) => string,
  config: OptionConfig<T, Extra> = {},
): (Option & Extra)[] => {
  const { filter, extend } = config;
  return (items ?? [])
    .filter((item) => (filter ? filter(item) : true))
    .map((item) => toOption(getValue(item), getLabel(item), extend ? extend(item) : undefined));
};

export const resolveDefaultOptions = <T>(
  items: readonly T[] | null | undefined,
  options: Option[],
  getValue: (item: T) => string,
  getLabel: (item: T) => string,
) =>
  (items ?? []).map((item) => {
    const value = getValue(item);
    return options.find((opt) => opt.value === value) ?? toOption(value, getLabel(item));
  });

const parseWeekdaysFromRule = (rule?: string | null) => {
  if (!rule) return [] as EventFormValues['recurrenceWeekdays'];
  const match = rule.match(/BYDAY=([^;]+)/);
  if (!match) return [] as EventFormValues['recurrenceWeekdays'];
  return match[1].split(',').filter(Boolean) as EventFormValues['recurrenceWeekdays'];
};

export const extractRecurrenceFrequency = (rule?: string | null): 'daily' | 'weekly' | 'monthly' => {
  if (!rule) return 'weekly';
  if (rule.includes('FREQ=DAILY')) return 'daily';
  if (rule.includes('FREQ=MONTHLY')) return 'monthly';
  return 'weekly';
};

/**
 * Extracts the recurrence weekdays from an event and translates them from UTC to local time.
 * This ensures the user sees the weekdays in their local timezone, which is how they think about the schedule.
 */
export const extractRecurrenceWeekdays = (event?: EventSummary | null) => {
  const weekdays = parseWeekdaysFromRule(event?.recurrenceRule);
  
  if (event?.startTime) {
    const date = new Date(event.startTime);
    
    if (weekdays.length) {
      // Translate UTC weekdays back to local weekdays for display
      return translateWeekdaysFromUTC(weekdays as WeekdayCode[], date);
    }
    
    // Default to the local day of the start time
    return [WEEKDAY_OPTIONS[date.getDay()].code];
  }
  
  return weekdays.length ? weekdays : [];
};

export const extractRecurrenceEnd = (event?: EventSummary | null) => {
  if (!event?.recurrenceEndsAt) return undefined;
  return format(new Date(event.recurrenceEndsAt), 'yyyy-MM-dd');
};

export const isValidUuid = (value?: string | null) =>
  typeof value === 'string' && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(value);

export const parseDateTimeValue = (value?: string | Date) => {
  if (!value) return undefined;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : new Date(value);
  }
  const normalized = value.length === 16 ? `${value}:00` : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

export const alignEndDateWithStart = (
  startValue: string | Date | undefined,
  endValue: string | Date | undefined,
  toInput: (date: Date) => string,
) => {
  const start = parseDateTimeValue(startValue);
  if (!start) {
    const parsedEnd = parseDateTimeValue(endValue);
    return parsedEnd ? toInput(parsedEnd) : undefined;
  }
  const end = parseDateTimeValue(endValue) ?? new Date(start);
  end.setFullYear(start.getFullYear(), start.getMonth(), start.getDate());
  return toInput(end);
};

export const getLockedDateFromStart = (startValue?: string | Date) => {
  const start = parseDateTimeValue(startValue);
  if (!start) return undefined;
  return new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0);
};

export type EventFormDefaultsParams = {
  initialEvent: EventSummary | null;
  categoryOptions: Option[];
  hostOptions: Option[];
  defaultStartIso: string;
  defaultEndIso: string;
  defaultGroupOptions: Option[];
  defaultClassOptions: Option[];
  defaultAttendeeOptions: Option[];
  scopedEditingAvailable: boolean;
  toDateTimeInput: (date: Date) => string;
};

export const buildEventFormDefaultValues = (params: EventFormDefaultsParams): EventFormValues => {
  const {
    initialEvent,
    categoryOptions,
    hostOptions,
    defaultStartIso,
    defaultEndIso,
    defaultGroupOptions,
    defaultClassOptions,
    defaultAttendeeOptions,
    scopedEditingAvailable,
    toDateTimeInput,
  } = params;
  const parsedStart = parseDateTimeValue(initialEvent?.startTime);
  const parsedEnd = parseDateTimeValue(initialEvent?.endTime);
  return {
    title: initialEvent?.title ?? '',
    description: initialEvent?.description ?? '',
    eventKind: initialEvent?.eventKind ?? 'group-class',
    eventCategoryId: initialEvent?.eventCategoryId ?? categoryOptions[0]?.value ?? '',
    hostUserId: initialEvent?.hostUserId ?? hostOptions[0]?.value ?? '',
    location: initialEvent?.location ?? '',
    startDateTime: parsedStart ? toDateTimeInput(parsedStart) : defaultStartIso,
    endDateTime: parsedEnd ? toDateTimeInput(parsedEnd) : defaultEndIso,
    groupIds: defaultGroupOptions,
    classIds: defaultClassOptions,
    attendeeIds: defaultAttendeeOptions,
    // Must match `isSeriesEvent` in event-form.tsx. A lone recurrenceRule is NOT
    // enough: detached occurrences keep a stale rule with no template behind it,
    // and showing them as recurring makes the form promise edits it can't apply.
    // Toggling this on for such an event promotes it to a real series instead.
    isRecurring: Boolean(initialEvent?.isTemplate || initialEvent?.seriesTemplateId),
    recurrenceFrequency: extractRecurrenceFrequency(initialEvent?.recurrenceRule),
    recurrenceWeekdays: extractRecurrenceWeekdays(initialEvent),
    recurrenceEndDate: extractRecurrenceEnd(initialEvent) ?? null,
    applyScope: scopedEditingAvailable ? 'occurrence' : undefined,
    forceResetAllAssociations: false,
    journeyId: '',
  };
};

